import { useState } from "react";
import { currency, idr, plotPrice } from "@/lib/api";

const LABEL = { sold: "SOLD", reserved: "BOOKED" };
const centroid = (poly) => {
  const n = poly.length;
  return [poly.reduce((s, p) => s + p[0], 0) / n, poly.reduce((s, p) => s + p[1], 0) / n];
};

const Price = ({ listing, p, sel }) =>
  p.price_on_request ? (
    <span className={`font-mono text-xs ${sel ? "text-cream" : "text-terracotta"}`}>Price on request</span>
  ) : (
    <span className={`text-right font-mono text-xs ${sel ? "text-cream" : "text-terracotta"}`}>
      <span className="block text-sm">{idr(plotPrice(listing, p.sqm, p.price_idr_per_are || undefined).idr)}</span>
      <span className={sel ? "text-cream/80" : "text-moss"}>≈ {currency(plotPrice(listing, p.sqm, p.price_idr_per_are || undefined).usd)}</span>
    </span>
  );

export const PlotMap = ({ development, listing, selected, onSelect }) => {
  const plans = development.plans || [];
  const [planId, setPlanId] = useState(plans[0]?.id);
  const [hover, setHover] = useState(null);
  const plan = plans.find((p) => p.id === planId) || plans[0];
  if (!plan) return null;
  const plots = plan.plots;
  const available = plots.filter((p) => p.status === "available");
  const fs = plan.label_size || 2.4;

  return (
    <div data-testid="plot-map">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-pine">Plot map</p>
          <p className="mt-1 text-sm text-moss">{plan.title}</p>
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 font-mono text-[10px] uppercase tracking-[0.16em] text-moss">
          <span><i className="mr-1.5 inline-block h-2.5 w-2.5 rounded-sm bg-terracotta/40 ring-1 ring-terracotta" />Available · {available.length}</span>
          <span className="text-[#b07a1f]">Booked · {plots.filter((p) => p.status === "reserved").length}</span>
          <span className="text-[#b3261e]">Sold · {plots.filter((p) => p.status === "sold").length}</span>
        </div>
      </div>

      {plans.length > 1 && (
        <div className="mt-4 flex gap-1 rounded-full bg-sand/70 p-1 sm:w-fit" data-testid="plan-tabs">
          {plans.map((pl) => (
            <button
              key={pl.id}
              type="button"
              onClick={() => setPlanId(pl.id)}
              data-testid={`plan-tab-${pl.id}`}
              className={`rounded-full px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors ${
                pl.id === plan.id ? "bg-pine text-cream" : "text-moss hover:text-pine"
              }`}
            >
              {pl.title.split(" · ")[0]}
            </button>
          ))}
        </div>
      )}

      <div className="relative mt-4 overflow-hidden rounded-2xl border border-pine/10 bg-white">
        <img src={plan.image} alt={plan.title} className="block w-full" draggable={false} />
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
          {plots.map((p) => {
            const active = selected?.number === p.number || hover === p.number;
            const isAvail = p.status === "available";
            const [cx, cy] = p.label || (p.polygon.length ? centroid(p.polygon) : [0, 0]);
            return (
              <g
                key={p.number}
                data-testid={`plot-${p.number}`}
                data-status={p.status}
                onMouseEnter={() => setHover(p.number)}
                onMouseLeave={() => setHover(null)}
                onClick={() => isAvail && onSelect(selected?.number === p.number ? null : p)}
                style={{ cursor: isAvail ? "pointer" : "default" }}
              >
                {p.polygon.length > 0 && (
                  <polygon
                    points={p.polygon.map((pt) => pt.join(",")).join(" ")}
                    fill={isAvail && active ? "rgba(196,98,63,0.45)" : "transparent"}
                    stroke={isAvail && active ? "#c4623f" : "transparent"}
                    strokeWidth={0.5}
                    vectorEffect="non-scaling-stroke"
                    style={{ transition: "fill 200ms" }}
                  />
                )}
                {!isAvail && (
                  <text x={cx} y={p.label ? cy : cy + 6} textAnchor="middle" fontSize={fs} fontFamily="monospace" fontWeight="700" fill={p.status === "sold" ? "#b3261e" : "#b07a1f"} letterSpacing="0.3" style={{ pointerEvents: "none" }}>
                    {LABEL[p.status]}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid="plot-list">
        {available.map((p) => {
          const isSel = selected?.number === p.number;
          return (
            <button
              key={p.number}
              type="button"
              onClick={() => onSelect(isSel ? null : p)}
              onMouseEnter={() => setHover(p.number)}
              onMouseLeave={() => setHover(null)}
              data-testid={`plot-option-${p.number}`}
              aria-pressed={isSel}
              className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors ${
                isSel ? "border-terracotta bg-terracotta text-cream" : "border-pine/10 bg-white hover:border-terracotta/60"
              }`}
            >
              <span>
                <span className="block font-display text-lg">Plot {p.number}</span>
                <span className={`font-mono text-[10px] uppercase tracking-[0.14em] ${isSel ? "text-cream/80" : "text-moss"}`}>
                  {p.sqm.toLocaleString()} m² · {(p.sqm / 100).toFixed(1)} ara
                </span>
              </span>
              <Price listing={listing} p={p} sel={isSel} />
            </button>
          );
        })}
      </div>
      <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.14em] text-sage">
        Tap an available plot to add it to your request · Phase 2 plots at {idr(listing.price_idr_per_are)} per are (≈ {currency(listing.price_idr_per_are / listing.fx_idr_per_usd)}) · Plot 10 at {idr(90_000_000)} per are · masterplan from {currency(listing.price_per_sqm)} / m²
      </p>
    </div>
  );
};
